import React, { useState } from 'react';
import {
    Box,
    TextField,
    Button,
    Typography,
    Container,
    Alert,
    Link,
    CircularProgress,
} from '@mui/material';
import { resetAudiencePassword } from '@virtual-cinema/client-lib';

function ForgotPasswordPage() {
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const validateForm = () => {
        if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setError('Valid email is required.');
            return false;
        }
        setError('');
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm() || isLoading) return;

        setIsLoading(true);
        try {
            const { error } = await resetAudiencePassword(email);

            if (error) {
                setError('An error occurred. Please try again.');
                return;
            }

            setSuccess(true);
            setError('');
        } catch (err) {
            console.error('Reset password error:', err);
            setError('An error occurred. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const commonTextFieldSx = {
        backgroundColor: '#333',
        borderRadius: '4px',
        width: '100%',
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: '#555',
            },
            '&:hover fieldset': {
                borderColor: '#666',
            },
        },
    };

    return (
        <Container maxWidth="sm">
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    marginTop: '5rem',
                    padding: '2rem',
                    borderRadius: '8px',
                    backgroundColor: 'black',
                    color: 'white',
                    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.5)',
                }}
            >
                <Typography variant="h4" sx={{ fontWeight: 600, marginBottom: '1rem' }}>
                    Reset Password
                </Typography>

                {!success ? (
                    <>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                color: '#999',
                                textAlign: 'center',
                                fontSize: '1.1rem',
                                lineHeight: '1.5',
                                marginBottom: '2rem',
                            }}
                        >
                            Enter your email address and we'll send you instructions to reset your password
                        </Typography>

                        {error && (
                            <Alert severity="error" sx={{ width: '100%', marginBottom: '1rem', backgroundColor: 'rgba(211, 47, 47, 0.1)' }}>
                                {error}
                            </Alert>
                        )}

                        <form style={{ width: '100%' }} onSubmit={handleSubmit}>
                            <Box sx={{ width: '100%', marginBottom: '1rem' }}>
                                <Typography sx={{ color: 'white', marginBottom: '4px' }}>
                                    Email
                                </Typography>
                                <TextField
                                    variant="outlined"
                                    fullWidth
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    sx={commonTextFieldSx}
                                />
                            </Box>

                            <Button
                                variant="contained"
                                color="primary"
                                fullWidth
                                sx={{ marginTop: '1rem', height: '42px' }} // Added fixed height for consistency
                                type="submit"
                                disabled={isLoading} // Disable button when loading
                            >
                                {isLoading ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                                        Sending...
                                    </Box>
                                ) : (
                                    'Send Reset Instructions'
                                )}
                            </Button>
                        </form>
                    </>
                ) : (
                    <Box sx={{ textAlign: 'center' }}>
                        <Alert
                            severity="success"
                            sx={{
                                width: '100%',
                                marginBottom: '1.5rem',
                                backgroundColor: 'rgba(46, 125, 50, 0.1)'
                            }}
                        >
                            If an account exists with this email, you will receive password reset instructions shortly.
                        </Alert>
                        <Typography
                            variant="body1"
                            sx={{
                                color: '#999',
                                marginBottom: '1.5rem'
                            }}
                        >
                            Please check your email inbox (and spam/junk folder) for instructions to reset your password.
                        </Typography>
                    </Box>
                )}

                <Box sx={{ marginTop: '1.5rem', textAlign: 'center' }}>
                    <Link
                        href="/login"
                        sx={{
                            color: 'lightblue',
                            textDecoration: 'none',
                            '&:hover': {
                                textDecoration: 'underline'
                            }
                        }}
                    >
                        Back to Login
                    </Link>
                </Box>
            </Box>
        </Container>
    );
}

export default ForgotPasswordPage;